import { defaultParamsSerializer, formatDates, handleApiResponse } from '@/functions';
import { IFetchDataParams, IAccountingUser } from '@/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import { Paginated } from '@/lib/interface';
import routes from '@/lib/routes';

type TParams = IFetchDataParams & {
    type?: string[];
    status?: string[];
    search?: string;
    config?: {
        staleTime?: number;
    }
};
export type TResponseData = Paginated<IAccountingUser>;

export const useRequestGetAccountingUsers = (params?: TParams, run = true) => {

    // Default value 0 - always refetch on filter change to show shimmer
    const staleTime = params?.config?.staleTime ?? 0;

    const { invalidate } = useInvalidateQueries();
    const { isFetching, isPlaceholderData, data, error } = useQuery({
        queryKey: [routes.api.accountingUsers, params, run],
        queryFn: ({ queryKey: [_url, _params, _run] }) =>
            !_run
                ? Promise.resolve(null)
                : fetchQuery(_url as string, _params as TParams),
        staleTime
    });

    const accountingUsers = (data?.data ?? []);

    const fetchQuery = (url: string, params?: TParams) =>
        apiClient
            .get<TResponseData>(url, params, { paramsSerializer: defaultParamsSerializer })
            .then(handleApiResponse)
            .then(data => formatDates(data) as TResponseData);

    const fetchData = (params?: TParams) =>
        fetchQuery(routes.api.accountingUsers, params);

    const refresh = () =>
        invalidate(routes.api.accountingUsers);

    return ({
        data: data as TResponseData,
        isLoading: isFetching && !data,
        isFetching: isFetching || isPlaceholderData,
        accountingUsers,
        fetchData,
        refresh,
        error
    });

};
